import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import TranscriptTurnFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/FormData/TranscriptTurnFormData';
import type TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';
import reviseTranscriptTurn from '@Application/Beneficiaries/Transcripts/reviseTranscriptTurn';
import ReviseTranscriptTurn from '@Application/Beneficiaries/Transcripts/Commands/ReviseTranscriptTurn';
import type { TranscriptTurnRevisionFailedAction } from '@Application/Beneficiaries/Transcripts/Actions/transcriptTurnRevisionFailed';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import AudioEditContext from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/Providers/TurnRevisionProvider/Context';
import { confirmDialog } from '@verclam/ui-kit';

type Props = {
	children: React.ReactNode;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
};

const TurnRevisionProvider: React.FC<Props> = ({ children, subject, interviewId }) => {
	const dispatch = useDispatch();
	const [editingTurnId, setEditingTurnId] = React.useState<number | null>(null);
	const [originalText, setOriginalText] = React.useState<string | null>(null);
	const [formData, setFormData] = React.useState<TranscriptTurnFormData>(() =>
		TranscriptTurnFormData.createEmpty(),
	);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const cancel = React.useCallback((): void => {
		setEditingTurnId(null);
		setOriginalText(null);
		setFormData(TranscriptTurnFormData.createEmpty());
	}, []);

	const openTurn = React.useCallback((turn: TranscriptTurnView): void => {
		setEditingTurnId(turn.id);
		setOriginalText(turn.text);
		setFormData(TranscriptTurnFormData.fromView(turn));
	}, []);

	const changeText = React.useCallback((value: string): void => {
		setFormData((current) => current.setText(value));
	}, []);

	const submit = React.useCallback((): void => {
		if (editingTurnId === null || originalText === null) {
			return;
		}

		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		if (formData.text === originalText) {
			cancel();
			return;
		}

		setIsSubmitting(true);

		dispatch(
			reviseTranscriptTurn(
				new ReviseTranscriptTurn(facilityId, subject, interviewId, editingTurnId, formData.text),
			),
		)
			.then(() => {
				cancel();
			})
			.catch((action: TranscriptTurnRevisionFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'TRANSCRIPT_TURN_REVISION_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [editingTurnId, originalText, formData, dispatch, facilityId, subject, interviewId, cancel]);

	const hasUnsavedEdit = React.useMemo(
		() => editingTurnId !== null && formData.text !== originalText,
		[editingTurnId, formData.text, originalText],
	);

	const runGuarded = React.useCallback(
		(action: () => void): void => {
			if (!hasUnsavedEdit) {
				action();
				return;
			}

			confirmDialog({
				header: 'Correction en cours',
				message: "Une correction est en cours et n'a pas été enregistrée. La perdre ?",
				acceptLabel: 'Continuer sans enregistrer',
				acceptSeverity: 'danger',
				accept: () => {
					cancel();
					action();
				},
			});
		},
		[hasUnsavedEdit, cancel],
	);

	const contextValue = React.useMemo(
		() => ({
			editingTurnId,
			formData,
			isSubmitting,
			hasUnsavedEdit,
			openTurn,
			changeText,
			submit,
			cancel,
			runGuarded,
		}),
		[
			editingTurnId,
			formData,
			isSubmitting,
			hasUnsavedEdit,
			openTurn,
			changeText,
			submit,
			cancel,
			runGuarded,
		],
	);

	return <AudioEditContext.Provider value={contextValue}>{children}</AudioEditContext.Provider>;
};

export default TurnRevisionProvider;
